import { Switch, Card, Typography, Button } from "antd";
import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import axiosInstance from "../../variables/api/axiosInstance";
import { LOGGED_USER } from "../../variables/constants";
import { SafetyOutlined, MailOutlined, MobileOutlined, WhatsAppOutlined, PhoneOutlined, EnvironmentOutlined } from '@ant-design/icons';
import entete from "../../assets/img/entete_pool.png";
import poolins from "../../assets/img/poolins.jpg";
import {
  evaluatePasswordChangeRequirement,
  PASSWORD_CHANGE_REASON,
} from "../../utils/passwordChangePolicy";

const { Title, Text, Paragraph } = Typography;

const ConfirmOTP = () => {
  const [confirmCode, setConfirmCode] = useState("000000");
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef([]);
  const [useGAuth, setUseGAuth] = useState(false);
  const navigate = useNavigate();
  const [insurances, setInsurances] = useState([]);

  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }

    axiosInstance.get(`/api/organisme/all/assurances`).then(response => {
      if (response.data) {
        setInsurances(response.data);
      }
    }).catch(error => {
      console.error("Erreur lors de la récupération des logos des assurances:", error);
    });
  }, []);

  const handleChange = (e, index) => {
    const { value } = e.target;
    if (!/^\d$/.test(value) && value !== "") return;

    const newStr =
      confirmCode.substring(0, index) +
      value +
      confirmCode.substring(index + 1);
    setConfirmCode(newStr);

    if (value.length === 1 && index < inputRefs.current.length - 1) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault();

      const currentValue = inputRefs.current[index].value;

      if (currentValue) {
        inputRefs.current[index].value = '';
        const newStr =
          confirmCode.substring(0, index) +
          '0' +
          confirmCode.substring(index + 1);
        setConfirmCode(newStr);
      } else if (index > 0) {
        inputRefs.current[index - 1].focus();
        inputRefs.current[index - 1].value = '';
        const newStr =
          confirmCode.substring(0, index - 1) +
          '0' +
          confirmCode.substring(index);
        setConfirmCode(newStr);
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();
    const digits = pastedData.replace(/\D/g, "").slice(0, 6);

    if (digits.length > 0) {
      const newCode = digits.padEnd(6, "0");
      setConfirmCode(newCode);

      digits.split("").forEach((digit, idx) => {
        if (inputRefs.current[idx]) {
          inputRefs.current[idx].value = digit;
        }
      });

      const nextEmptyIndex = Math.min(digits.length, 5);
      if (inputRefs.current[nextEmptyIndex]) {
        inputRefs.current[nextEmptyIndex].focus();
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    const storedUser = JSON.parse(sessionStorage.getItem("user") || "{}");
    const emailFromStorage =
      storedUser?.email || sessionStorage.getItem("firstLoginEmail") || "";
    const usernameFromStorage = LOGGED_USER?.username || emailFromStorage;

    if (!useGAuth && !emailFromStorage) {
      toast("Adresse mail introuvable pour l'OTP. Reprenez la connexion.");
      setLoading(false);
      navigate("/connexion");
      return;
    }

    const codeAsNumber = Number(String(confirmCode).replace(/\D/g, ""));

    const confirmRequest = {
      codeId: useGAuth ? usernameFromStorage : emailFromStorage,
      confirmCode: codeAsNumber,
    };

    axiosInstance
      .post(
        useGAuth ? `/api/2fa/verify` : `/api/confirm-code`,
        JSON.stringify(confirmRequest)
      )
      .then((res) => {
        if (res.status === 200) {
          const token = res.data?.token;
          if (typeof token === "string" && token.includes(".")) {
            const [n, t, k] = token.split(".");
            sessionStorage.setItem("n", n);
            sessionStorage.setItem("t", t);
            sessionStorage.setItem("k", k);
          }
          sessionStorage.setItem("q", "1");
          let user = null;
          try {
            user = JSON.parse(sessionStorage.getItem("user") || "null");
          } catch (e) {
            user = null;
          }

          const decision = evaluatePasswordChangeRequirement(user);
          if (decision.shouldForce) {
            const message =
              decision.reason === PASSWORD_CHANGE_REASON.EXPIRED
                ? "Votre mot de passe est expiré. Veuillez en définir un nouveau"
                : "Veuillez définir votre nouveau mot de passe";
            toast.info(message);
            navigate("/first-connexion", { replace: true });
          } else {
            toast.success("Vous êtes connecté avec succès!");
            navigate("/");
          }
        } else {
          toast.error("Erreur lors de la validation du code OTP");
        }
      })
      .catch((err) => {
        if (
          err?.response?.status === 403 &&
          err?.response?.data?.code === "PASSWORD_CHANGE_REQUIRED"
        ) {
          toast.info("Première connexion : veuillez définir un nouveau mot de passe.");
          if (emailFromStorage) {
            sessionStorage.setItem("firstLoginEmail", emailFromStorage);
          }
          navigate("/first-connexion", { replace: true });
        } else if (
          err?.response?.status === 400 &&
          err?.response?.data?.code === "OTP_INVALID"
        ) {
          toast.error("Code OTP invalide. Réessayez.");
        } else {
          toast.error("Erreur lors de la validation du code OTP");
        }
      })
      .finally(() => setLoading(false));
  };

  function obfuscateEmail(email = "") {
    if (!email.includes("@")) return email;
    const [username, domain] = email.split("@");
    const visibleChars = 4;
    const safe = Math.max(0, username.length - visibleChars);
    const obfuscatedUsername = "*".repeat(safe) + username.slice(-visibleChars);
    return obfuscatedUsername + "@" + domain;
  }

  const displayedEmail =
    JSON.parse(sessionStorage.getItem("user") || "{}")?.email ||
    sessionStorage.getItem("firstLoginEmail") ||
    "";

  const phoneNumber = JSON.parse(sessionStorage.getItem("user") || "{}")?.otpPhoneNumber;

  return (
    <div style={{
      height: '100vh',
      backgroundImage: 'url(assets/img/landing.png)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Overlay */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(36, 33, 102, 0.12)',
        backdropFilter: 'blur(10px)',
        zIndex: 0
      }} />

      <ToastContainer position="top-center" />

      {/* Main content area */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        position: 'relative',
        zIndex: 1,
        minHeight: 0
      }}>
        <div style={{
          width: '100%',
          maxWidth: '1400px',
          display: 'grid',
          gridTemplateColumns: '300px auto 300px',
          gap: '40px',
          alignItems: 'center'
        }}>
          {/* Left Section - PoolIns */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '15px',
            alignItems: 'center'
          }}>
            <img
              src={poolins}
              alt="PoolIns"
              style={{
                maxWidth: '180px',
                width: '100%',
                height: 'auto',
                borderRadius: '16px',
                filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.5))'
              }}
            />
            <Text style={{
              color: 'white',
              fontSize: '14px',
              fontWeight: '600',
              display: 'block',
              fontStyle: 'italic',
              letterSpacing: '0.3px',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
              textAlign: 'center'
            }}>
              Solution intégrée des assurances
            </Text>
          </div>

          {/* Center Section - OTP Form */}
          <Card style={{
            width: '500px',
            background: 'white',
            borderRadius: '24px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
            border: 'none',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              background: 'linear-gradient(135deg, #242166 0%, #3b82f6 100%)',
              padding: '28px 28px',
              marginTop: '-24px',
              marginLeft: '-24px',
              marginRight: '-24px',
              marginBottom: '24px',
              textAlign: 'center'
            }}>
              <SafetyOutlined style={{ fontSize: '42px', color: 'white', marginBottom: '12px' }} />
              <Title level={2} style={{
                color: 'white',
                margin: 0,
                fontSize: '26px',
                fontWeight: '700'
              }}>
                Authentification à 2 facteurs
              </Title>
              <Text style={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '14px'
              }}>
                Sécurisez votre accès
              </Text>
            </div>

            <div style={{ padding: '0 8px 8px' }}>
              {/* Message d'information */}
              <div style={{
                background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                borderRadius: '14px',
                padding: '16px',
                marginBottom: '20px',
                border: '1px solid #bfdbfe'
              }}>
                <Paragraph style={{
                  margin: 0,
                  color: '#1e40af',
                  fontSize: '13px',
                  lineHeight: '1.5',
                  textAlign: 'center'
                }}>
                  Code de vérification à 6 chiffres envoyé
                </Paragraph>

                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '10px',
                  marginTop: '12px',
                  flexWrap: 'wrap'
                }}>
                  {phoneNumber && (
                    <div style={{
                      background: 'white',
                      padding: '6px 14px',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <MobileOutlined style={{ color: '#242166', fontSize: '14px' }} />
                      <Text strong style={{ color: '#242166', fontSize: '12px' }}>
                        ****{phoneNumber?.trim?.()?.slice?.(-3)}
                      </Text>
                    </div>
                  )}
                  {displayedEmail && (
                    <div style={{
                      background: 'white',
                      padding: '6px 14px',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <MailOutlined style={{ color: '#242166', fontSize: '14px' }} />
                      <Text strong style={{ color: '#242166', fontSize: '12px' }}>
                        {obfuscateEmail(displayedEmail)}
                      </Text>
                    </div>
                  )}
                </div>
              </div>

              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '20px' }}>
                  <Text style={{
                    display: 'block',
                    marginBottom: '14px',
                    color: '#242166',
                    fontWeight: '600',
                    fontSize: '13px',
                    textAlign: 'center'
                  }}>
                    Entrez le code à 6 chiffres
                  </Text>

                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    justifyContent: 'center',
                    marginBottom: '16px'
                  }}>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <input
                        key={i}
                        type="tel"
                        maxLength="1"
                        onChange={(e) => handleChange(e, i)}
                        onKeyDown={(e) => handleKeyDown(e, i)}
                        onPaste={handlePaste}
                        ref={(el) => (inputRefs.current[i] = el)}
                        style={{
                          width: '46px',
                          height: '52px',
                          textAlign: 'center',
                          fontSize: '22px',
                          fontWeight: '700',
                          border: '2px solid #e5e7eb',
                          borderRadius: '12px',
                          outline: 'none',
                          transition: 'all 0.3s ease',
                          color: '#242166'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#242166';
                          e.target.style.boxShadow = '0 0 0 3px rgba(36, 33, 102, 0.1)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#e5e7eb';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                    ))}
                  </div>

                  {LOGGED_USER?.gsecret && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      marginBottom: '14px'
                    }}>
                      <Switch
                        checkedChildren="Code Email"
                        unCheckedChildren="Google Authenticator"
                        onChange={() => setUseGAuth(!useGAuth)}
                        checked={useGAuth}
                        style={{
                          background: useGAuth ? '#242166' : undefined
                        }}
                      />
                    </div>
                  )}
                </div>

                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  size="large"
                  block
                  style={{
                    background: 'linear-gradient(135deg, #242166 0%, #3b82f6 100%)',
                    borderRadius: '12px',
                    height: '46px',
                    fontSize: '15px',
                    fontWeight: '600',
                    border: 'none',
                    boxShadow: '0 4px 12px rgba(36, 33, 102, 0.3)',
                    marginBottom: '16px'
                  }}
                >
                  {loading ? 'Vérification en cours...' : 'Confirmer mon identité'}
                </Button>

                <div style={{
                  textAlign: 'center',
                  padding: '14px',
                  background: '#f8fafc',
                  borderRadius: '12px'
                }}>
                  <Text style={{ color: '#64748b', fontSize: '12px' }}>
                    Vous n'avez pas reçu le code ?{' '}
                    <a
                      href="/connexion"
                      style={{
                        color: '#242166',
                        fontWeight: '600',
                        textDecoration: 'none'
                      }}
                    >
                      Renvoyer
                    </a>
                  </Text>
                </div>
              </form>
            </div>
          </Card>

          {/* Right Section - Placeholder for symmetry */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            alignItems: 'center'
          }}>
            {/* Pool Logo */}
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '24px',
              textAlign: 'center',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
            }}>
              <img
                src={entete}
                alt="Pool TPVM-VT GIE"
                style={{
                  width: '100%',
                  maxWidth: '280px',
                  height: 'auto'
                }}
              />
            </div>

            {/* Contact Information - directly on background */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '14px',
              width: '100%'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px'
              }}>
                <EnvironmentOutlined style={{
                  color: '#3b82f6',
                  fontSize: '20px',
                  marginTop: '2px',
                  filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))'
                }} />
                <Text style={{
                  color: 'white',
                  fontSize: '15px',
                  lineHeight: '1.6',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  fontWeight: '500'
                }}>
                  Rue Gérard DULPHY, Lomégan derrière Radio Pyramide<br />
                  01 B.P. 4866, Lomé - TOGO
                </Text>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <PhoneOutlined style={{
                  color: '#3b82f6',
                  fontSize: '20px',
                  filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))'
                }} />
                <Text style={{
                  color: 'white',
                  fontSize: '15px',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  fontWeight: '500'
                }}>
                  +228 92 62 66 66
                </Text>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <WhatsAppOutlined style={{
                  color: '#25D366',
                  fontSize: '20px',
                  filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))'
                }} />
                <Text style={{
                  color: 'white',
                  fontSize: '15px',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  fontWeight: '500'
                }}>
                  +228 70 48 11 11
                </Text>
              </div>

              {/* Email */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <MailOutlined style={{
                  color: '#3b82f6',
                  fontSize: '20px',
                  filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))'
                }} />
                <Text style={{
                  color: 'white',
                  fontSize: '15px',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  fontWeight: '500'
                }}>
                  poolvtgie@pooltpv.tg
                </Text>
              </div>

              <div style={{
                marginTop: '12px',
                paddingTop: '16px',
                borderTop: '2px solid rgba(255, 255, 255, 0.3)',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text style={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontSize: '14px',
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                    fontWeight: '600'
                  }}>
                    NIF:
                  </Text>
                  <Text style={{
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                  }}>
                    1000 16 1028
                  </Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text style={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontSize: '14px',
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                    fontWeight: '600'
                  }}>
                    RCC:
                  </Text>
                  <Text style={{
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                  }}>
                    2009 C 0771
                  </Text>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Section - Company Logos Marquee */}
      <div style={{
        padding: '20px 0',
        position: 'relative',
        zIndex: 1,
        overflow: 'hidden'
      }}>
        <div style={{
          width: '100%',
          overflow: 'hidden',
          position: 'relative'
        }}>
          <style>
            {`
              @keyframes marquee {
                0% {
                  transform: translateX(0);
                }
                100% {
                  transform: translateX(-50%);
                }
              }
              
              .marquee-content {
                display: flex;
                gap: 30px;
                animation: marquee 30s linear infinite;
                will-change: transform;
              }
              
              .marquee-content:hover {
                animation-play-state: paused;
              }
              
              .company-logo-card {
                background: white;
                border-radius: 12px;
                padding: 16px;
                min-width: 140px;
                height: 85px;
                display: flex;
                align-items: center;
                justify-content: center;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
                transition: transform 0.3s ease, box-shadow 0.3s ease;
                flex-shrink: 0;
              }
              
              .company-logo-card:hover {
                transform: translateY(-4px);
                box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4);
              }
            `}
          </style>

          <div className="marquee-content">
            {/* First set of logos */}
            {Array.isArray(insurances) && insurances.map((ins, i) => (
              <div key={`logo-1-${i}`} className="company-logo-card">
                <img
                  src={`data:image/png;base64,${ins.logoDisplay}`}
                  alt={ins.raisonSociale || `Logo ${i + 1}`}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '60px',
                    objectFit: 'contain'
                  }}
                />
              </div>
            ))}

            {/* Duplicate set for seamless loop */}
            {Array.isArray(insurances) && insurances.map((ins, i) => (
              <div key={`logo-2-${i}`} className="company-logo-card">
                <img
                  src={`data:image/png;base64,${ins.logoDisplay}`}
                  alt={ins.raisonSociale || `Logo ${i + 1}`}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '60px',
                    objectFit: 'contain'
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmOTP;